import * as a from '@mitranim/js/all.mjs'

// These vars are inlined into HTML in SSR.
export const ENV_KNOWN = a.Emp()

// On server launch, we validate that these vars are present.
export const ENV_REQ = new Set()

function envGet(key) {return globalThis.process.env[key]}
function envOpt(key) {return ENV_KNOWN[key] = envGet(key)}

function envReq(key) {
  ENV_REQ.add(key)
  return envOpt(key)
}

function oneOf(val, vals) {
  if (a.includes(vals, val)) return val
  throw Error(`expected one of ${a.show(vals)}, got ${a.show(val)}`)
}

function envReqCond(key, req) {return a.reqBool(req) ? envReq(key) : envOpt(key)}

export const ATTR_BODY_EMPTY = `data-body-empty`

export const ELEM_ID_FALLBACK = `cs-fallback`
export const ELEM_ID_MODALS   = `cs-modals`
export const ELEM_ID_PLANS    = `plans`

export const EVENT_MODAL_HIDE           = `modal-hide`
export const EVENT_NOTICE_HIDE          = `notice-hide`
export const EVENT_ONE_TIME_CLOSE       = `one-time-close`

export const PROD                       = envOpt(`NODE_ENV`) === `production`
export const VERSION                    = envOpt(`VERSION`) || (PROD ? `` : `dev`)
export const NODE_ENV                   = envOpt(`NODE_ENV`)
export const PUBLIC_RELEASE             = a.boolOpt(envOpt(`PUBLIC_RELEASE`))
export const ROBOTS_TXT_ALLOWED         = PUBLIC_RELEASE && a.boolOpt(envOpt(`ROBOTS_TXT_ALLOWED`))
export const LOG_DEBUG                  = a.boolOpt(envOpt(`LOG_DEBUG`))
export const LOG_TYPE                   = envOpt(`LOG_TYPE`)
export const LOG_TYPE_JSON              = `json`

export const API_SERVER_URL             = envReq(`API_SERVER_URL`)
export const SERVER_ORIGIN              = envOpt(`SERVER_ORIGIN`)
export const DEV_SKIP_PRERENDER         = !PROD && a.boolOpt(envOpt(`DEV_SKIP_PRERENDER`))
export const DEV_DISABLE_IFRAMES        = !PROD && a.boolOpt(envOpt(`DEV_DISABLE_IFRAMES`))
export const DEV_AI_MOCK                = !PROD && a.boolOpt(envOpt(`DEV_AI_MOCK`))
export const MOBILE_DOMAIN_URI_PREFIX   = envOpt(`MOBILE_DOMAIN_URI_PREFIX`)
export const ANDROID_PACKAGE_NAME       = envOpt(`ANDROID_PACKAGE_NAME`)
export const IOS_PACKAGE_NAME           = envOpt(`IOS_PACKAGE_NAME`)
export const IOS_APP_STORE_ID           = envOpt(`IOS_APP_STORE_ID`)
export const MOBILE_URL_SCHEME          = envOpt(`MOBILE_URL_SCHEME`)
export const ENABLE_TEST_ATTRS          = a.boolOpt(envOpt(`ENABLE_TEST_ATTRS`))
export const ENABLE_SW                  = a.boolOpt(envOpt(`ENABLE_SW`))
export const ENABLE_ACTS                = a.boolOpt(envOpt(`ENABLE_ACTS`))

export const MAIN_PAGE_QUESTIONNAIRE_MODE = oneOf(envOpt(`MAIN_PAGE_QUESTIONNAIRE_MODE`), [undefined, `classic`, `typeform`])
export const MAIN_PAGE_TYPEFORM_ID        = envReqCond(`MAIN_PAGE_TYPEFORM_ID`, MAIN_PAGE_QUESTIONNAIRE_MODE === `typeform`)

export const FEAT_ARTICLE_PUBLICATION_DATE = a.vac(!PROD)
export const FEAT_YOUTUBE_STREAMING        = a.vac(!PROD)
export const FEAT_NEW                      = a.vac(!PROD)
export const FEAT_STREAM_STAT              = undefined
export const SIMPLE_BUY_FLOW               = a.boolOpt(envOpt(`SIMPLE_BUY_FLOW`))
export const AUTO_LOAD_MORE                = a.boolOpt(envOpt(`AUTO_LOAD_MORE`)) ?? true
export const FEAT_FESTIVE_ONE_TIME_MODAL   = a.boolOpt(envOpt(`FEAT_FESTIVE_ONE_TIME_MODAL`))
export const FEAT_FESTIVE                  = a.boolOpt(envOpt(`FEAT_FESTIVE`))

export const MOBILE_LINK_APPLE    = envOpt(`MOBILE_LINK_APPLE`)
export const MOBILE_LINK_ANDROID  = envOpt(`MOBILE_LINK_ANDROID`)

export const FAKE_LIKES = a.boolOpt(envOpt(`FAKE_LIKES`))

export const PAID_LISTING_ENABLED     = a.boolOpt(envOpt(`PAID_LISTING_ENABLED`))
export const ENABLE_PRIME_MEMBERSHIP  = false
export const DISCOUNT_TYPE_PRIME      = `prime`

// In the range: 0 to 1
export const NOT_PERFECT_BEFORE = 0.5

export const AFTER_N_MESSAGE_PROMO = 3
export const SERVICES_IN_MSGS_PER_PROMO = 3

/*
Should match severity levels used by Stackdriver (case-insensitive):
https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#logseverity
*/
export const SEVERITY_INFO  = `INFO`
export const SEVERITY_DEBUG = `DEBUG`
export const SEVERITY_ERROR = `ERROR`

// TODO rename to `cs_session_id`.
export const COOKIE_NAME_SESSION       = `session_id`
export const HEADER_NAME_SESSION       = `X-Cs-Session`

export const HEADER_NAME_TEST_MODE     = `X-Cs-Test-Mode`
export const HEADER_NAME_PRIVATE_TOKEN = `X-Cs-Private-Token`

export const COOKIE_NAME_LANG = `cs_lang`
export const HEADER_NAME_LANG = `X-Cs-Lang`

export const COOKIE_NAME_DATA = `cs_data`

export const COOKIE_NAME_DEVICE_ID = `cs_device_id`
export const HEADER_NAME_DEVICE_ID = `X-Cs-Device-Id`

export const HEADER_NAME_PLATFORM = `X-Cs-Device-Platform`
export const HEADER_NAME_APP_TYPE = `X-Cs-App-Type`
export const HEADER_NAME_APP_VER = `X-App-Version`

/*
Every key is both a URL parameter name and a cookie name.
Using `snake_case` query parameters is inconsistent with
our general `camelCase` style, but has the accidental
advantage of avoiding collisions with `camelCase` query
parameters used on feed pages. For example, `cs_lang`
will never collide with `languageCode`.
*/
export const QUERY_COOKIES = [
  `ref_code`,
  `reffer_person_id`,
  `cs_lang`,
  `utm_id`,
  `utm_source`,
  `utm_medium`,
  `utm_campaign`,
  `utm_term`,
  `utm_content`,
]

export const IMAGE_SERVER_URL = envReq(`IMAGE_SERVER_URL`)
export const IMAGE_LAZY_LOAD = a.boolOpt(envOpt(`IMAGE_LAZY_LOAD`))
export const MAP_API_KEY = envReq(`MAP_API_KEY`)

export const EXTERNAL_INTEGRATIONS_ENABLED = a.vac(a.boolOpt(envOpt(`EXTERNAL_INTEGRATIONS_ENABLED`)))

/*
Google Tag Manager property ID (don't confuse with GTAG gtag.js)
Can be provided to `gtag.js`.
https://support.google.com/tagmanager/answer/7582054
Disabled while we don't use GTM.

export const TAG_API_KEY = (EXTERNAL_INTEGRATIONS_ENABLED ? envReq : envOpt)(`TAG_API_KEY`)
*/

/*
Google Analytics property ID.
Can be provided to `gtag.js`.
*/
export const GA_MEASUREMENT_ID = envOpt(`GA_MEASUREMENT_ID`)

export const GTAG_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`GTAG_ENABLED`))

/*
Google Ads property ID. Can be provided to `gtag.js`.
Disabled while we don't use Google Ads.

export const GADS_GTAG_API_KEY = envOpt(`GADS_GTAG_API_KEY`)
*/

export const SENTRY_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`SENTRY_ENABLED`))

// Can be found at https://sentry.io/settings/binarysages/projects/binarysages/keys/
export const SENTRY_DSN = envOpt(`SENTRY_DSN`)

export const SEND_IN_BLUE_ENABLE_API    = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`SEND_IN_BLUE_ENABLE_API`))
export const SEND_IN_BLUE_V3_CLIENT_KEY = envOpt(`SEND_IN_BLUE_V3_CLIENT_KEY`)

/*
The "language" parameter affects input placeholder and search results in the
places autocomplete widget.

Ideally we would load only the libraries required for the current page, but
Google's API seems opposed to dynamically loading additional Maps libraries.
For now, we load them all, once.
*/
export const GOOGLE_MAPS_SCRIPT_URL = a.url(`https://maps.googleapis.com/maps/api/js`)
  .setQuery({
    key: MAP_API_KEY,
    language: `en`,
    // Used by Google Places Autocomplete.
    libraries: `places`,
  })
  .href

export const GOOGLE_MAPS_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`GOOGLE_MAPS_ENABLED`))

export const GOOGLE_TIME_ZONE_URL = `https://maps.googleapis.com/maps/api/timezone/json`

// Unused, TODO remove.
export const AMAZON_ADS_SCRIPT_URL = `https://z-na.amazon-adsystem.com/widgets/onejs?MarketPlace=US`
export const AMAZON_ASSOC_ID = `amzn_assoc_ad_div_adunit0`

export const GOOGLE_ADS_NUM = 2

export const ENABLE_AMAZON_ADS = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`ENABLE_AMAZON_ADS`))
export const ENABLE_GOOGLE_ADS = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`ENABLE_GOOGLE_ADS`))

export const VIMEO_SCRIPT_URL = `https://player.vimeo.com/api/player.js`
export const VIMEO_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`VIMEO_ENABLED`))

/*
Styles for TypeForm are removed:
  * Should be loaded in a safe way only in case of TypeForm is necessary.
  * Now it overrides some styles by accident.
*/
export const TYPEFORM_SCRIPT_URL = `https://cdn.jsdelivr.net/npm/@typeform/embed@1.35.1/build/index.min.js`
export const TYPEFORM_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`TYPEFORM_ENABLED`))

export const DAILY_SCRIPT_URL = `https://cdn.jsdelivr.net/npm/@daily-co/daily-js@0.21.0/dist/daily-iframe.min.js`
export const DAILY_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`DAILY_ENABLED`))

export const POET_SCRIPT_URL = `https://www.poeticmetric.com/pm.js`
export const POET_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`POET_ENABLED`))

export const DAILY_ROOM_BASE_URL = envReq(`DAILY_ROOM_BASE_URL`)

export const STRIPE_PUBLIC_KEY = envReq(`STRIPE_PUBLIC_KEY`)
export const STRIPE_COUNTRY = envOpt(`STRIPE_COUNTRY`)
export const STRIPE_CUSTOMER_PORTAL_URL = envOpt(`STRIPE_CUSTOMER_PORTAL_URL`)

export const PAY_PAL_CLIENT_ID = envReq(`PAY_PAL_CLIENT_ID`)

/*
Unused, disabled.
export const PAY_PAL_CONNECT_URL = envReq(`PAY_PAL_CONNECT_URL`)
export const PAY_PAL_CONNECT_ACCOUNT_CALLBACK_URL = a.url(SERVER_ORIGIN).setPath(`/api/v2/persons/current/paypal_account/set`)
*/

export const MAGIC_LINK_DEFAULT_CODE_LENGTH = 6
export const OAUTH_PATH_GOOGLE = `/api/v2/auth/google/init`
export const OAUTH_PATH_ETSY = `/api/v2/auth/etsy/init`
export const OAUTH_PATH_FACEBOOK = `/api/v2/auth/facebook/init`
export const OAUTH_PATH_PAY_PAL = `/api/v2/auth/pay_pal/init`
export const API_PATH_STRIPE_CONNECT_URL = `/api/v2/persons/current/stripe_connect/url`

// Corresponds to `cs-core` enum `ident_provider`.
// For non-SQL code, the type is `slug`.
export const IDENT_PROVIDER_LOCAL = `local`
export const IDENT_PROVIDER_GOOGLE = `google`
export const IDENT_PROVIDER_FACEBOOK = `facebook`
export const IDENT_PROVIDER_APPLE = `apple`
export const IDENT_PROVIDER_ETSY = `etsy`

// Google ReCAPTCHA v3 Site Key
export const RECAPTCHA_SITE_KEY = envOpt(`RECAPTCHA_SITE_KEY`)
export const RECAPTCHA_SCRIPT_URL = envOpt(`RECAPTCHA_SCRIPT_URL`)
export const RECAPTCHA_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`RECAPTCHA_ENABLED`))

export const PRACT_PAGE_BRAND = a.boolOpt(envOpt(`PRACT_PAGE_BRAND`))

export const POST_HOG_KEY = envOpt(`POST_HOG_KEY`)

export const ENABLE_FB_PIXEL = a.boolOpt(envOpt(`ENABLE_FB_PIXEL`))
export const FB_PIXEL_ID = envOpt(`FB_PIXEL_ID`)

export const IMAGE_WIDTH_SMALL  = 640
export const IMAGE_WIDTH_MEDIUM = 800
export const IMAGE_WIDTH_LARGE  = 1920
export const IMAGE_WIDTH_GIANT  = 4320

// London: {lng: -0.118092, lat: 51.509865}.
export const DEFAULT_GEO_LONGITUDE = -0.118092
export const DEFAULT_GEO_LATITUDE = 51.509865

// Types of notifications
export const NOTICE_INFO    = `NOTICE_INFO`
export const NOTICE_SUCCESS = `NOTICE_SUCCESS`
export const NOTICE_WARNING = `NOTICE_WARNING`
export const NOTICE_ERROR   = `NOTICE_ERROR`
export const NOTICE_DANGER  = `NOTICE_DANGER`

// Copied from backend code. Must be kept in sync.
export const PASSWORD_LENGTH_MIN = 8
export const PASSWORD_LENGTH_MAX = 20

export const PERSON_NAME_PLACEHOLDER = `Joahim Van Der Saar`
export const ORG_NAME_PLACEHOLDER = `Name`
export const USER_BIO_PLACEHOLDER         = ``
export const PRACTITIONER_BIO_PLACEHOLDER = `e.g. Dr. Doe received an undergraduate degree in psychology at Southern College, then pursued graduate training in counseling psychology at the University of Maryland and completed a doctorate in clinical psychology at the Western School of Professional Psychology. He currently has specialty certification in clinical psychology by the American Board of Professional Psychology, and previously in addictions. His clinical interests have included work with children, adolescents, adults, diagnostic assessment and addictions. Dr. Doe uses a broad range of cognitive behavioral and insight-oriented tactics in the psychotherapy and counseling work. Dr. Doe has taught a wide range of graduate psychology and counseling courses at the major national universities, as well as internationally in Hungary and Poland. He has lectured and led training seminars and workshops nationally and internationally in Europe and East Asia. His research and publication interests have included addictions, psychometrics, clinical training and supervision, and the integration of spiritual values in psychotherapy and supervision.`
export const ORGANIZATION_BIO_PLACEHOLDER = `e.g. The ABC was organized in 1979. ABC is the leading scientific and professional organization representing psychology in Canada, with more than 104,000 researchers, educators, clinicians, consultants and students as its members. Vision: A humanity where awareness of diverse human needs, behaviours and aspirations ignite legislation, policies and programs for individuals, organizations and communities. Mission: Promoting research, knowledge and the application of science for the good of society through advocacy, support and collaboration.`
export const PERSON_EMAIL_PLACEHOLDER = `someone@example.com`
export const ORG_EMAIL_PLACEHOLDER = `funnycorn@amazon.com`
export const PHONE_PLACEHOLDER = `44 123 456 78 90`
export const PASSWORD_PLACEHOLDER = `At least ${PASSWORD_LENGTH_MIN} characters`
export const CONFIRM_PASSWORD_PLACEHOLDER = `Repeat Password`
export const WEBSITE_PLACEHOLDER = `https://example.com`
export const LOCATION_PLACEHOLDER = `Enter geographic location`
export const COMMENT_PLACEHOLDER = `Leave your comment`
export const FIND_YOUR_PRACTICE = `Practices`
export const PAYMENT_SUCCESSFUL = `Payment successful!`
export const THANK_YOU = `Thank you!`
export const CONTINUE_SHOPPING = `Continue shopping`
export const CONTINUE = `Continue`
export const CONFIRM_BOOKING = `Confirm booking`
export const CANCEL_BOOKING = `Cancel booking`
export const REJECT_BOOKING = `Reject booking`

// Placeholders
export const AVATAR_PLACEHOLDER                 = `/images/no-avatar.jpg`
export const AVATAR_PLACEHOLDER_NEW             = `/images/svg/icons/user.svg`
export const IMAGE_PLACEHOLDER                  = `/images/svg/placeholder.svg`
export const ORGANIZATION_BG_PLACEHOLDER_SMALL  = `/images/bg/placeholders/organization-small.jpg`
export const ORGANIZATION_BG_PLACEHOLDER_BIG    = `/images/bg/placeholders/organization-big.jpg`

// App Promo
export const APP_LINK_APP_STORE   = `https://apps.apple.com/us/app/core-spirit/id1560983829`
export const APP_LINK_GOOGLE_PLAY = `https://play.google.com/store/apps/details?id=com.corespirit.app`

// Must be kept in sync with server data.
export const PROMOTION_TYPE_SERVICE = `service`
export const PROMOTION_TYPE_ARTICLE_LINK = `article_link`

// Must be kept in sync with server data.
export const SLUG_PROMOTION_AD_FREE_PROFILE         = `ad_free_profile`
export const SLUG_PROMOTION_FAVORITE_PRACTITIONERS  = `favorite_practitioners`
export const SLUG_PROMOTION_PAID_LISTING            = `paid_listing`
export const SLUG_PROMOTION_PREFERRED_PRACTITIONERS = `preferred_practitioners`
export const SLUG_PROMOTION_STEAL_THE_MAP           = `steal_the_map`
export const SLUG_PROMOTION_PLAN_FREE               = `pkg_free`
export const SLUG_PROMOTION_PLAN_STD                = `pkg_std`
export const SLUG_PROMOTION_PLAN_ADV                = `pkg_adv`
export const SLUG_PROMOTION_PLAN_PRO                = `pkg_pro`
export const SLUG_PROMOTION_PLAN_STD_AB1            = `pkg_std_ab1`
export const SLUG_PROMOTION_PLAN_ADV_AB1            = `pkg_adv_ab1`
export const SLUG_PROMOTION_PLAN_PRO_AB1            = `pkg_pro_ab1`

export const SLUG_PROMO_SERVICE_PPC = `service_ppc`
export const SLUG_PROMO_ARTICLE_LINK = `article_link`

export const DOM_AVAILABLE = typeof window === `object` && a.isObj(a.laxStruct(window).document)
export const CUSTOM_ELEM_AVAILABLE = DOM_AVAILABLE && typeof customElements === `object` && a.isObj(customElements)
export const INTERSECTION_AVAILABLE = DOM_AVAILABLE && typeof IntersectionObserver === `function`
export const COOKIES_AVAILABLE = DOM_AVAILABLE && a.isStr(document.cookie)
export const GEOLOCATION_AVAILABLE = typeof navigator === `object` && a.isObj(a.laxStruct(navigator).geolocation)
export const ENABLE_COOKIES_PANEL = a.boolOpt(envOpt(`ENABLE_COOKIES_PANEL`))

export const FIREBASE_API_URL = envOpt(`FIREBASE_API_URL`)
export const FIREBASE_WEB_API_KEY = envOpt(`FIREBASE_WEB_API_KEY`)

export const PROMO_NAG_PERIOD_SEC = a.intOpt(envOpt(`PROMO_NAG_PERIOD_SEC`)) ?? a.SEC_IN_HOUR
export const PROMO_NAG_COUNTDOWN = a.intOpt(envOpt(`PROMO_NAG_COUNTDOWN`)) ?? 4

// Prev: `10 USD`. Removed by marketing request.
export const MIN_SERVICE_PRICE        = 0
export const PLATFORM_FEE             = 0

export const MEMBERSHIP_SERVICES_COST = `Free`
export const ONE_DOLLAR               = `$1`

// Referral
export const REFER_FRIEND_BONUS       = `20%`
export const REFER_FRIEND_GET_1000    = `$1000`

// Strings for Meta and OpenGraph
export const TERMS_OF_SERVICE_TITLE   = `Terms Of Service`
export const PRIVACY_POLICY_TITLE     = `Privacy Policy`

// Note: these links must be absolute because they're used in meta tags that
// expect full URLs.
export const META_LOGO_URL = a.url(SERVER_ORIGIN).setPath(`images/logo.png`)
export const META_COVER_URL = a.url(SERVER_ORIGIN).setPath(`images/meta-cover.jpg`)

export const YANDEX = `yandex-verification`
export const YANDEX_VERIFICATION = `5b62ed6b56200c22`

export const INPUT_DEBOUNCE = 1024
export const CAROUSEL_INTERVAL = 1024 * 8
export const DELAY_POPUP_SUCCESS = 4000

export const MODERATION_STATUS_PENDING        = `pending`
export const MODERATION_STATUS_APPROVED       = `approved`
export const MODERATION_STATUS_REJECTED       = `rejected`
export const MODERATION_STATUS_RETRACTED      = `retracted`

export const BOOKING_STATUS_INITIAL           = `initial`
export const BOOKING_STATUS_PAYMENT_PENDING   = `payment_pending`
export const BOOKING_STATUS_PAYMENT_FAILED    = `payment_failed`
export const BOOKING_STATUS_PAYMENT_SUCCEEDED = `payment_succeeded`
export const BOOKING_STATUS_PAYMENT_CANCELED  = `payment_canceled`
export const BOOKING_STATUS_PROVIDED          = `provided`
export const BOOKING_STATUS_CANCELED          = `canceled`

export const PURCHASE_STATUS_INITIAL  = `initial`
export const PURCHASE_STATUS_PAID     = `paid`
export const PURCHASE_STATUS_REFUNDED = `refunded`

// TODO: migrate from `event.keyCode` to `event.key`.
export const KEY_CODE_TAB         = 9
export const KEY_CODE_ENTER       = 13
export const KEY_CODE_ESCAPE      = 27
export const KEY_CODE_SPACE       = 32
export const KEY_CODE_ARROW_LEFT  = 37
export const KEY_CODE_ARROW_UP    = 38
export const KEY_CODE_ARROW_RIGHT = 39
export const KEY_CODE_ARROW_DOWN  = 40
export const KEY_CODE_J           = 74
export const KEY_CODE_K           = 75

export const KEY_SPACE = ` `
export const KEY_ENTER = `Enter`
export const KEY_ESCAPE = `Escape`
export const KEY_TAB = `Tab`
export const KEY_ARROW_UP = `ArrowUp`
export const KEY_ARROW_DOWN = `ArrowDown`
export const KEY_ARROW_LEFT = `ArrowLeft`
export const KEY_ARROW_RIGHT = `ArrowRight`
export const KEY_BACKSPACE = `Backspace`
export const KEY_DELETE = `Delete`

// Copied from the old Node backend. TODO move to the schema.
export const USER_BIO_MAX_LENGTH = 65536

// TODO Count words instead of characters.
export const ARTICLE_MIN_LENGTH = 1000

export const MAX_PERSON_CATEGORIES = undefined
export const MAX_ENTITY_CATEGORIES = 1
export const MAX_LANGS = 5

export const MAX_PERSON_CONCERNS = undefined
export const MAX_ENTITY_CONCERNS = 1

export const ARTICLE_SELF_PUBLISH = false

export const DEFAULT_ARTICLE_READING_TIME = 5

export const INPUT_MAX_LENGTH = 255

// Should match `cs-core` `TITLE_MAX_LENGTH`.
export const INPUT_MAX_LENGTH_NAME = 128

export const PREVIEW_TEXT_MAX_LENGTH = 256

export const IS_BTN_AUTH_LOGIN = {is: `btn-auth-login`}
export const IS_BTN_AUTH_REG   = {is: `btn-auth-reg`}

export const CTA_BACK_HOME             = `Back Home`
export const CTA_LOGIN                 = `Login`
export const CTA_REGISTER              = `Register`
export const CTA_LOGIN_REGISTER        = `${CTA_LOGIN} or ${CTA_REGISTER}`
export const HTTP_NOT_FOUND_TITLE      = `Page Not Found`
export const HTTP_NOT_FOUND_MESSAGE    = `The page you are looking for doesn't exist or has been moved.`
export const HTTP_UNAUTHORIZED_TITLE   = `Unauthorized`
export const HTTP_UNAUTHORIZED_MESSAGE = `You don't have permissions to view this page. Please ${CTA_LOGIN_REGISTER}.`
export const UNKNOWN_TITLE             = `Error`
export const UNKNOWN_TEXT              = `Something went wrong`

// https://en.wikipedia.org/wiki/ISO_4217
// https://en.wikipedia.org/wiki/Category:Currency_symbols
export const CURRENCY_USD = {code: `usd`, symbol: `$`}
export const CURRENCY_EUR = {code: `eur`, symbol: `€`}
export const CURRENCY_GBP = {code: `gbp`, symbol: `£`}
export const CURRENCY_CNY = {code: `cny`, symbol: `¥`}
export const CURRENCY_RUB = {code: `rub`, symbol: `₽`}

export const CURRENCIES = {
  [CURRENCY_USD.code]: CURRENCY_USD,
  [CURRENCY_EUR.code]: CURRENCY_EUR,
  [CURRENCY_GBP.code]: CURRENCY_GBP,
  [CURRENCY_CNY.code]: CURRENCY_CNY,
  [CURRENCY_RUB.code]: CURRENCY_RUB,
}

export const CURRENCY_DEFAULT = CURRENCY_USD

export const CENTS_IN_UNIT = 100

export const FRESHNESS_DEFAULT = a.MS_IN_MIN * 8

export const PERIOD_MONTH  = `P1M`
export const PERIOD_ANNUAL = `P1Y`

/*
Typographic rules:

  * To indicate a pause or explanation, use spaced emdash:

    one — two

  * To indicate a numeric range or datetime range, use unspaced endash:

    1234–5678
*/
export const NON_BREAKING_SPACE = `\u00A0`
export const ENDASH = `–` // `\u2013`
export const EMDASH = `—` // `\u2014`

export const SELECTABLE_NONE = {value: undefined, title: `—`}
export const SELECTABLE_ALL = {value: undefined, title: `All`}

export const CERTIFICATE_SELECTABLE = [
  {value: undefined, title: `All`},
  {value: true,      title: `Only with certificates`},
]

export const SERVICE_ONLINE_SELECTABLES = [
  {value: true,  title: `Online`},
  {value: false, title: `Offline`},
]

export const SERVICE_RELATION_TYPE_ONE_TO_ONE   = `one_to_one`
export const SERVICE_RELATION_TYPE_ONE_TO_MANY  = `one_to_many`
export const SERVICE_RELATION_TYPE_MANY_TO_MANY = `many_to_many`

export const PRACTITIONER_YEARS_OF_PRACTICE_RANGE_SELECTABLES = [
  {title: `All durations`,      value: undefined},
  {title: `Less than 3 years`,  value: {max: 3}},
  {title: `From 3 to 5 years`,  value: {min: 3, max: 5}},
  {title: `From 5 to 10 years`, value: {min: 5, max: 10}},
  {title: `More than 10 years`, value: {min: 10}},
]

export const PRACTITIONER_OFFERS_SELECTABLES = [
  {value: undefined,  title: `All`,      values: undefined},
  {value: `articles`, title: `Articles`, values: {hasPublicArticles: true}},
  {value: `services`, title: `Services`, values: {hasPublicSessions: true}},
  {value: `events`,   title: `Events`,   values: {hasPublicEvents:   true}},
]

export const SERVICE_DURATION_RANGE_SELECTABLES = [
  {value: undefined, title: `All durations`,             values: undefined},
  {value: `30`,      title: `Less than 30 minutes`,      values: {durationMax: a.minToSec(30)}},
  {value: `30-60`,   title: `From 30 minutes to 1 hour`, values: {durationMin: a.minToSec(30), durationMax: a.minToSec(60)}},
  {value: `60+`,     title: `More than 1 hour`,          values: {durationMin: a.minToSec(60)}},
]

export const SERVICE_PARTICIPANT_AGE_SELECTABLES = [
  {value: 0,  title: `0+`},
  {value: 13, title: `13+`},
  {value: 16, title: `16+`},
  {value: 18, title: `18+`},
  {value: 21, title: `21+`},
]

export const SERVICE_NATURE_SESSION    = `session`
export const SERVICE_NATURE_EVENT      = `event`
export const SERVICE_NATURE_VIDEO      = `video`
export const SERVICE_NATURE_BOOK       = `book`
export const SERVICE_NATURE_COURSE     = `course`
export const SERVICE_NATURE_SUB        = `sub`

export const SERVICE_STYLE_COURSE = `course`

/*
Should match ent types defined in `cs-core`. Should include only the names of
actual database tables. For invented types which don't match database tables,
see the "virt type" enum below.
*/
export const ENT_TYPE_PERSON = `person`
export const ENT_TYPE_ARTICLE = `article`
export const ENT_TYPE_SERVICE = `service`
export const ENT_TYPE_BOOKING = `booking`
export const ENT_TYPE_PROMO = `promo`
export const ENT_TYPE_GIFT_CARD = `gift_card`

export const VIRT_TYPE_ARTICLE = ENT_TYPE_ARTICLE
export const VIRT_TYPE_SERVICE = ENT_TYPE_SERVICE
export const VIRT_TYPE_EVENT = `event`
export const VIRT_TYPE_VIDEO = `video`
export const VIRT_TYPE_BOOK = `book`
export const VIRT_TYPE_COURSE = `course`
export const VIRT_TYPE_SECTION = `section`
export const VIRT_TYPE_PRACTITIONER = `practitioner`
export const VIRT_TYPE_DEPOSIT = `deposit`

// Ordering directions understood by our server.
export const DIR_ASC = `asc`
export const DIR_DESC = `desc`

export const SERVICE_SORT_SELECTABLES = [
  {value: `priceCents desc`, title: `Price: High to Low`},
  {value: `priceCents asc`,  title: `Price: Low to High`},
  {value: `updatedAt desc`,  title: `Most recent`},
  {value: `updatedAt asc`,   title: `Oldest`},
  {value: `reaction desc`,   title: `Top rated`},
  // {value: '', title: 'Recommendations'},
  // {value: '', title: 'Top viewed'},
]

export const PRACTITIONER_SORT_SELECTABLES = [
  {value: `updatedAt desc`,     title: `Most recent`},
  {value: `updatedAt asc`,      title: `Oldest`},
  {value: `avgRating desc`,     title: `Top rated`},
  // {value: '',                title: 'Top viewed'},
]

export const ARTICLE_SORT_SELECTABLES = [
  {value: `updatedAt desc`, title: `Most recent`},
  {value: `updatedAt asc`,  title: `Oldest`},
  {value: `reaction desc`,    title: `Top rated`},
  // {value: '', title: 'Top viewed'},
]

export const ORGANIZATION_SORT_SELECTABLES = [
  {value: `updatedAt desc`, title: `Registrations: New to Old`},
  {value: `updatedAt asc`,  title: `Registrations: Old to New`},
  {value: `avgRating desc`, title: `Top rated`},
  // {value: 'topViewed',      title: 'Top viewed'},
]

export const VIDEO_SORT_SELECTABLES = [
  {value: `priceCents desc`, title: `Price: High to Low`},
  {value: `priceCents asc`,  title: `Price: Low to High`},
  {value: `updatedAt desc`,  title: `Most recent`},
  {value: `updatedAt asc`,   title: `Oldest`},
]

export const AGE_SELECTABLES = [
  {value: 10, title: `10`},
  {value: 20, title: `20`},
  {value: 30, title: `30`},
  {value: 40, title: `40`},
  {value: 50, title: `50`},
  {value: 60, title: `60`},
]

/*
TODO move these rules to the backend to avoid duplicating the enums between
backend and frontend.
*/
export const SERVICE_OPTIONS = [
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_ONE],   growthType: [`personal`],     isOnline: [true],  style: [            `seminar`,               `workshop`, `class`, `course`, `webinar`,            `therapy_session`, `coaching_session`, `consultation`, `healing_session`, `mentorship_session` ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_ONE],   growthType: [`personal`],     isOnline: [false], style: [            `seminar`,               `workshop`, `class`, `course`,                       `therapy_session`, `coaching_session`, `consultation`, `healing_session`, `mentorship_session` ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_ONE],   growthType: [`professional`], isOnline: [true],  style: [`training`, `seminar`,               `workshop`, `class`, `course`, `webinar`, `lecture`, `therapy_session`, `coaching_session`, `consultation`,                    `mentorship_session` ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_ONE],   growthType: [`professional`], isOnline: [false], style: [`training`, `seminar`,               `workshop`, `class`, `course`,            `lecture`, `therapy_session`, `coaching_session`, `consultation`,                    `mentorship_session` ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`personal`],     isOnline: [true],  style: [            `seminar`,               `workshop`, `class`, `course`, `webinar`                                                                                                            ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`personal`],     isOnline: [false], style: [            `seminar`,               `workshop`, `class`, `course`                                                                                                                       ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`professional`], isOnline: [true],  style: [`training`, `seminar`,               `workshop`, `class`, `course`, `webinar`, `lecture`                                                                                                 ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`professional`], isOnline: [false], style: [`training`, `seminar`,               `workshop`, `class`, `course`,            `lecture`                                                                                                 ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`personal`],     isOnline: [true],  style: [`training`, `seminar`,               `workshop`, `class`, `course`, `webinar`                                                                                                            ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`personal`],     isOnline: [false], style: [`training`, `seminar`,               `workshop`, `class`, `course`                                                                                                                       ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`professional`], isOnline: [true],  style: [`training`, `seminar`,               `workshop`, `class`, `course`, `webinar`, `lecture`                                                                                                 ]},
  {nature: [SERVICE_NATURE_SESSION], relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`professional`], isOnline: [false], style: [`training`, `seminar`,               `workshop`, `class`, `course`,            `lecture`                                                                                                 ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`personal`],     isOnline: [true],  style: [`training`,            `conference`, `workshop`, `class`,           `webinar`,                                            `ceremony`                                                     ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`personal`],     isOnline: [false], style: [`training`,            `conference`, `workshop`, `class`,                         `retreat`, `book_signing`,              `ceremony`                                                     ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`professional`], isOnline: [true],  style: [`training`, `seminar`, `conference`,             `class`,           `webinar`                                                                                                            ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`professional`], isOnline: [false], style: [`training`, `seminar`, `conference`, `workshop`, `class`,                         `retreat`, `book_signing`                                                                              ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`personal`],     isOnline: [true],  style: [`training`,            `conference`,                                `webinar`                                                                                                            ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`personal`],     isOnline: [false], style: [`training`,            `conference`,                                              `retreat`, `book_signing`, `festival`                                                                  ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`professional`], isOnline: [true],  style: [`training`,            `conference`, `workshop`                                                                                                                                          ]},
  {nature: [SERVICE_NATURE_EVENT],   relationType: [SERVICE_RELATION_TYPE_MANY_TO_MANY], growthType: [`professional`], isOnline: [false], style: [`training`,            `conference`, `workshop`,                                  `retreat`, `book_signing`, `festival`                                                                  ]},
  {nature: [SERVICE_NATURE_VIDEO],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`personal`],     isOnline: [true],  style: [`training`, `seminar`,               `workshop`, `class`, `course`, `webinar`                                                                                                            ]},
  {nature: [SERVICE_NATURE_VIDEO],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`professional`], isOnline: [true],  style: [`training`, `seminar`,               `workshop`, `class`, `course`, `webinar`, `lecture`                                                                                                 ]},
  {nature: [SERVICE_NATURE_VIDEO],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`personal`],     isOnline: [true],  style: [`training`,            `conference`                                                                                                                                                      ]},
  {nature: [SERVICE_NATURE_VIDEO],   relationType: [SERVICE_RELATION_TYPE_ONE_TO_MANY],  growthType: [`professional`], isOnline: [true],  style: [`training`,            `conference`                                                                                                                                                      ]},
]

/*
Remove this flag once `n.deleteService` is functional. Properly implementing
service deletion requires differentiating pending bookings from finished
bookings, which requires bookings to have a datetime, which is currently not
implemented on the frontend.
*/
export const SERVICE_DELETION_IMPLEMENTED = false

export const LIST_SIZE_SEARCH_THRESHOLD = 10

export const CALENDAR_MONTHLY_MODE_MONTH = `month`
export const CALENDAR_MONTHLY_MODE_WEEKS = `weeks`

export const CALENDAR_LEVEL_YEAR  = `year`
export const CALENDAR_LEVEL_MONTH = `month`
export const CALENDAR_LEVEL_WEEK  = `week`
export const CALENDAR_LEVEL_DAY   = `day`

/*
TODO: consider deprecating tzmodes. In practice, we always use local date-times,
which is the dominant use case for our users. The JS `Date` API supports
constructing date objects from numeric inputs only in the local timezone.
`Date.UTC` makes timestamps; instantiating such timestamps as `Date` makes
objects with the local timezone. The UTC mode complicates our internals and
can't be fully on par with the local mode.

What would be more useful is support for arbitrary timezones. In the JS `Date`
API, the only way to instantiate dates with arbitrary timezones is from
strings, which would require us to make dates, format them as strings with
custom numeric timezone offsets, then parse those strings back. None of this
seems ideal. At the time of writing, there's a proposal for an upcoming
alternative API, but it's going to take years.
*/
export const TZMODE_UTC = `utc`
export const TZMODE_LOCAL = `local`

export const IMAGE_SIZE_LIMIT_MB = 20

export const PIXEL_ERROR_MARGIN = 3

export const MAP_ZOOM_MIN       = 10
export const MAP_ZOOM_MAX       = 16
export const MAP_ZOOM_DEFAULT   = 13
export const MAP_THEME_DEFAULT  = {
  minZoomOverride: true,
  minZoom: MAP_ZOOM_MIN,
  maxZoom: MAP_ZOOM_MAX,

  overviewMapControl: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
}

export const IMAGE_UPLOAD_DEFAULT_SIZE = `880x490px or larger`
export const IMAGE_UPLOAD_DEFAULT_RESOLUTION = `72 DPI and higher`
export const IMAGE_UPLOAD_DEFAULT_FILE_SIZE = `up to 20 MB inclusively`

export const TITLE_MAX_COUNT_CHARACTERS               = 128
export const TEXTAREA_MAX_COUNT_CHARACTERS            = 1000
export const MESSAGE_MAX_COUNT_CHARACTERS             = 3000
export const CARD_DESCRIPTION_MID_COUNT_CHARACTERS    = 160
export const CARD_DESCRIPTION_MAX_COUNT_CHARACTERS    = 240
export const QUERY_SEARCH_INPUT_MAX_COUNT_CHARACTERS  = 140
export const TITLE_OF_REPLY_MAX_LENGTH                = 32
export const PREVIEW_LENGTH_SHORT                     = 16
export const PREVIEW_LENGTH_MID                       = 64

// TODO: move ratings to the backend.
export const RATING_EXPERTISE = {
  name: `expertise`,
  title: `Expertise`,
  description: `Skill or knowledge in a particular field. Level of practitioner’s competence in terms of knowledge, skill and experience.`,
}
export const RATING_SOCIAL_SKILLS = {
  name: `socialSkills`,
  title: `Social Skills`,
  description: `Any competence facilitating interaction and communication. Some of the examples are effective communication, conflict resolution, active listening, empathy and respect.`,
}
export const RATING_SERVICE_PRICE = {
  name: `servicePrice`,
  title: `Service Price`,
  description: `Did you find the price appropriate for this type and quality of service?`,
}
export const RATING_ACCURACY = {
  name: `accuracy`,
  title: `Accuracy`,
  description: `Please indicate whether the description of the service is accurate. Does it correspond with actual service provided by the practitioner?`,
}
export const RATING_EFFECTIVENESS = {
  name: `effectiveness`,
  title: `Effectiveness`,
  description: `Were you able to reach your goal for this session? Please indicate whether you achieved your expected outcome.`,
}
export const RATING_PROFESSIONALISM = {
  name: `professionalism`,
  title: `Professionalism`,
  description: ``,
}
export const RATING_DECENCY = {
  name: `decency`,
  title: `Decency`,
  description: ``,
}
export const RATING_SOCIABILITY = {
  name: `sociability`,
  title: `Sociability`,
  description: ``,
}
export const RATING_CLEANLINESS = {
  name: `cleanliness`,
  title: `Cleanliness`,
  description: ``,
}
export const SERVICE_RATINGS = [
  RATING_EXPERTISE,
  RATING_SOCIAL_SKILLS,
  RATING_SERVICE_PRICE,
  RATING_ACCURACY,
  RATING_EFFECTIVENESS,
]
export const PERSON_RATINGS = [
  RATING_ACCURACY,
  RATING_PROFESSIONALISM,
  RATING_DECENCY,
  RATING_SOCIABILITY,
  RATING_CLEANLINESS,
]

// Placeholder.
export const ARTICLE_RATINGS = [
  RATING_ACCURACY,
]

export const SELECTED_ITEMS_SIZE = 2
export const FEED_SIZE_SMALL = 3
export const FEED_SIZE_SHORT = 4
export const FEED_SIZE_MEDIUM = 6

// TODO rename to `STRIPE_TITLE`.
export const STRIPE_NAME = `Stripe`

export const STRIPE_COUNTRIES_SELECTABLES = [
  {value: `AU`, title: `Australia`},
  {value: `AT`, title: `Austria`},
  {value: `BE`, title: `Belgium`},
  {value: `BG`, title: `Bulgaria`},
  {value: `BR`, title: `Brazil `},
  {value: `CA`, title: `Canada`},
  {value: `CY`, title: `Cyprus`},
  {value: `CZ`, title: `Czech Republic`},
  {value: `DK`, title: `Denmark`},
  {value: `EE`, title: `Estonia`},
  {value: `FI`, title: `Finland`},
  {value: `FR`, title: `France`},
  {value: `DE`, title: `Germany`},
  {value: `GR`, title: `Greece`},
  {value: `HK`, title: `Hong Kong`},
  {value: `IN`, title: `India`},
  {value: `IE`, title: `Ireland`},
  {value: `IT`, title: `Italy`},
  {value: `JP`, title: `Japan`},
  {value: `LV`, title: `Latvia`},
  {value: `LT`, title: `Lithuania`},
  {value: `LU`, title: `Luxembourg`},
  {value: `MY`, title: `Malaysia`},
  {value: `MT`, title: `Malta`},
  {value: `MX`, title: `Mexico `},
  {value: `NL`, title: `Netherlands`},
  {value: `NZ`, title: `New Zealand`},
  {value: `NO`, title: `Norway`},
  {value: `PL`, title: `Poland`},
  {value: `PT`, title: `Portugal`},
  {value: `RO`, title: `Romania`},
  {value: `SG`, title: `Singapore`},
  {value: `SK`, title: `Slovakia`},
  {value: `SI`, title: `Slovenia`},
  {value: `ES`, title: `Spain`},
  {value: `SE`, title: `Sweden`},
  {value: `CH`, title: `Switzerland`},
  {value: `GB`, title: `United Kingdom`},
  {value: `US`, title: `United States`},
]
export const STRIPE_INPUT_STYLES = {
  style: {
    base: {
      backgroundColor: `#ffffff`,
      color: `#484848`,
      fontSize: `16px`,
      lineHeight: `36px`,
    },
    invalid: {
      color: `#cc0c0f`,
    },
  },
}

// Source: https://developer.paypal.com/docs/checkout/integration-features/customize-button/
export const PAY_PAL_INPUT_STYLES = {
  color: `blue`,
  shape: `rect`,
  size: `responsive`,
  height: 44,
  label: `pay`,
}

export const PAY_PAL_SUBSCRIPTION_INPUT_STYLES = {
  color: `blue`,
  shape: `rect`,
  size: `responsive`,
  height: 44,
  label: `subscribe`,
}

export const CATEGORY_SLUG_MAIN = `category`
export const CATEGORY_SLUG_CONCERN = `concern`
export const CATEGORY_DEPTH_MAIN = 3

export const SELECTED_CATEGORY_DEFAULT_TEXT = `All categories`
export const CAT_SEL_ALL = {title: SELECTED_CATEGORY_DEFAULT_TEXT}

export const STAR_RATING_MIN = 1
export const STAR_RATING_MAX = 5

export const DEFAULT_DECIMAL_PLACES = 2

export const SEARCH_TEXT_KEY = `searchText`

// Must match equivalent constants in `cs-core`, where relevant.
export const QUERY_KEY_ERR = `err`
export const QUERY_KEY_MSG = `msg`
export const QUERY_KEY_REDIR = `redir`
export const QUERY_KEY_SESSION_ID = `sessionId`
export const QUERY_KEY_MODE = `mode`
export const QUERY_KEY_KEY = `key`
export const QUERY_KEY_FRESH = `fresh`
export const QUERY_KEY_TYPE = `type`
export const QUERY_KEY_SLUG = `slug`
export const QUERY_KEY_INTERVAL = `interval`
export const QUERY_KEY_YEAR = `year`
export const QUERY_KEY_MONTH = `month`
export const QUERY_KEY_DAY = `day`
export const QUERY_KEY_PREFER_LATEST = `preferLatest`
export const QUERY_KEY_IS_PERSON_CREATED = `isPersonCreated`
export const QUERY_KEY_AUTHED = `authed`
export const QUERY_KEY_IDENT_PROVIDER = `identProvider`
export const QUERY_KEY_IS_FAVORITE = `isFavorite`

export const IMAGE_SERVER_QUERY_KEY_WIDTH = `width`

export const PLATFORM_ANDROID = `android`
export const PLATFORM_IOS = `ios`

export const NET_TIMEOUT_SHORT = 1000 * 4
export const NET_TIMEOUT_LONG = 1000 * 60

export const ERR_CODE_REQUIRES_PRACT = `REQUIRES_PRACT`

export const FILTER_PARTITION_MAIN  = `main`
export const FILTER_PARTITION_MODAL = `modal`

export const COOKIE_MAX_AGE_LONG = 60 * 60 * 24 * 365 * 17

export const RADIO_BOOL_SELECTABLES = [
  {title: `No`, value: false},
  {title: `Yes`, value: true},
]

export const RADIO_BOOL_SELECTABLES_OPT = [
  {title: `Not applicable`, value: undefined},
  ...RADIO_BOOL_SELECTABLES,
]

export const CHARGE_TITLE_TOTAL_TO_PAY = `Total to Pay:`
export const CHARGE_TITLE_TOTAL_COST = `Total Cost`
export const CHARGE_TITLE_DISCOUNT = `Discount`
export const CHARGE_TITLE_INTERNAL_BALANCE = `Internal Balance`

// Inconsistent with Gtag event `purchase`.
// Left this way for backwards compatibility.
export const SIB_EVENT_BUY = `buy`

export const NOTIF_ID = `db302426bd1845edb36dc25a9503d6f3`
export const GLOBAL_CHAN_ID = `310ac96bfac5425893a760d45723048b`
export const LINK_ELLIPSIS_LENGTH = 32
export const PAGE_SIZE_MAX = 48
export const UNICODE_VARIATION_SELECTOR_EMOJI = `\ufe0f`

export const ARIA_SORT = `aria-sort`
export const ARIA_SORT_ASC = `ascending`
export const ARIA_SORT_DESC = `descending`

export const NEWS_LIST_TOPIC_MEMBERSHIP = `membership`

export const SCHEMA_ORG_URL = `http://schema.org/`

export const BOOK_EXTS = [`pdf`, `epub`, `fb2`, `mobi`]
export const IMAGE_EXTS = [`jpg`, `jpeg`, `png`, `gif`]

// TODO fetch from server as part of global configuration.
export const EMAIL_SUBSCRIPTION_TARGET_INFO       = `info`
export const EMAIL_SUBSCRIPTION_TARGET_MARKETING  = `marketing`
export const EMAIL_SUBSCRIPTION_TARGET_BOOKING    = `booking`

// Hashes
export const HTML_ID_AVATAR           = `avatar`
export const HTML_ID_COVER_IMG        = `cover_image`
export const HTML_ID_BIO              = `bio`
export const HTML_ID_CATEGORIES       = `categories`
export const HTML_ID_CONCERNS         = `concerns`
export const HTML_ID_SHOWCASE_REVIEWS = `showcase_review`

export const PERSON_SLUG_EMENTOR = `ementor`
export const PERSON_ID_EMENTOR = `947ee5da53e24e738b4b0daa51ea110a`

export const BUK_KEY_MAIN = `main`
export const BUK_KEY_NEXT = `next`
export const BUK_KEY_REL = `rel`

/*
In `cs-core`, some parts of the buket API support decoding the inputs in two
ways: either as normal JSON, or as a JSON-encoded URL query. This content type
must be provided NOT as an HTTP header, but rather in the appropriate position
in the input to a buket request.
*/
export const CONTENT_TYPE_JSON_QUERY = `application/json; type=query`

// SYNC[csc.msg_types].
export const MSG_TYPE_PUBLIC = `public`
export const MSG_TYPE_MODERATION = `moderation`
export const MSG_TYPE_PERSONAL = `personal`
export const MSG_TYPE_NOTIF = `notif`

export const FIN_TRANSFER_TYPE_DEPOSIT = `deposit`
export const FIN_TRANSFER_TYPE_WITHDRAW = `withdrawal`
export const FIN_TRANSFER_TYPE_INTERNAL = `internal`

export const ORDER_TYPE_DEPOSIT = `deposit`
export const ORDER_TYPE_WITHDRAWAL = `withdrawal`
export const ORDER_TYPE_REFUND = `refund`

export const FILESTACK_ENABLED = EXTERNAL_INTEGRATIONS_ENABLED && a.boolOpt(envOpt(`FILESTACK_ENABLED`))

export const CAL_ABS_TIMESPAN_TYPE_AVAILABLE = `available`
export const CAL_ABS_TIMESPAN_TYPE_UNAVAILABLE = `unavailable`
export const CAL_ABS_TIMESPAN_TYPE_AUTOMATIC = `automatic`

export const REQUIRE_MOBILE_APP = a.boolOpt(envOpt(`REQUIRE_MOBILE_APP`))

/*
Used for demarkating HTML meta tags that must be replaced during pushstate
transitions.
*/
export const ATTR_HEAD_AUTO = `data-head-auto`

export const REG_MODE_PRACT = `practitioner`
export const REG_MODE_ORG = `organization`
export const REG_MODE_DEFAULT = REG_MODE_PRACT

export const LP_BANNER_DEFAULT = {value: `default`, class: `--bg-default`}

// Practitioner lp banner customization
export const LP_BANNER_SELECTABLES = [
  LP_BANNER_DEFAULT,
  {value: `purple-white`,             class: `--purple-white`},
  {value: `decent`,                   class: `--decent`},
  {value: `nighthawk`,                class: `--nighthawk`,        textClass: `fg-white`},
  {value: `miami-dolphins`,           class: `--miami-dolphins`},
  {value: `minnesota-vikings`,        class: `--minnesota-vikings`},
  {value: `joomla`,                   class: `--joomla`,           textClass: `fg-white`},
  {value: `green-and-blue`,           class: `--green-and-blue`},
  {value: `little-leaf`,              class: `--little-leaf`},
  {value: `back-to-earth`,            class: `--back-to-earth`},
  {value: `sage-persuasion`,          class: `--sage-persuasion`,  textClass: `fg-white`},
  {value: `lizard`,                   class: `--lizard`,           textClass: `fg-white`},
  {value: `piglet`,                   class: `--piglet`},
  {value: `dark-knight`,              class: `--dark-knight`,      textClass: `fg-white`},
  {value: `curiosity-blue`,           class: `--curiosity-blue`,   textClass: `fg-white`},
  {value: `green-to-dark`,            class: `--green-to-dark`,    textClass: `fg-white`},
  {value: `fresh-turboscent`,         class: `--fresh-turboscent`},
  {value: `koko-caramel`,             class: `--koko-caramel`},
  {value: `virgin-america`,           class: `--virgin-america`},
  {value: `turquoise-flow`,           class: `--turquoise-flow`},
  {value: `vine`,                     class: `--vine`},
  {value: `atlas`,                    class: `--atlas`},
  {value: `twitch`,                   class: `--twitch`,           textClass: `fg-white`},
  {value: `pastel-orange-at-the-sun`, class: `--pastel-orange-at-the-sun`},
  {value: `endless-river`,            class: `--endless-river`},
  {value: `purple-bliss`,             class: `--purple-bliss`,     textClass: `fg-white`},
  {value: `between-the-clouds`,       class: `--between-the-clouds`},
  {value: `friday`,                   class: `--friday`},
  {value: `serv-quick`,               class: `--serv-quick`,       textClass: `fg-white`},
  {value: `behongo`,                  class: `--behongo`},
  {value: `shore`,                    class: `--shore`},
  {value: `amethyst`,                 class: `--amethyst`},
  {value: `man-of-steel`,             class: `--man-of-steel`,     textClass: `fg-white`},
  {value: `neon-life`,                class: `--neon-life`},
  {value: `teal-love`,                class: `--teal-love`},
  {value: `cherryblossoms`,           class: `--cherryblossoms`},
  {value: `earthly`,                  class: `--earthly`},
]

export const LP_BANNER_CUSTOMIZATION = a.index(LP_BANNER_SELECTABLES, getValue)
function getValue(val) {return val.value}

// Caution: these are very approximate and vary between months and timezones.
export const MIN_IN_DAY = a.MIN_IN_HOUR * a.HOUR_IN_DAY // Missing from library.
export const HOUR_IN_MON = a.HOUR_IN_DAY * a.DAY_IN_MON
export const MIN_IN_MON = MIN_IN_DAY * a.DAY_IN_MON
export const SEC_IN_MON = a.SEC_IN_DAY * a.DAY_IN_MON

// Copied from `cs-core` DB constraint.
export const CAL_ABS_TIMESPAN_DISCRETIZATION = 15

export function getFileDownloadUrl(id) {
  return a.urlJoin(`/api/v2/files`, id)
}

export const ACT_TYPE_VIEW = `view`
export const ACT_TYPE_CLICK = `click`
export const ACT_TYPE_PAYMENT = `payment`

export const UI_KIND_ENT_PREVIEW = `ent_preview`
export const UI_KIND_ENT_DETAILS = `ent_details`

/*
Application-specific script types should use a MIME type to avoid collisions
with future additions to the DOM standard:

  https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script/type
*/
export const SCRIPT_TYPE_DELAYED = `application/delayed`

export const AI_MSG_ROLE_SYSTEM = `system`
export const AI_MSG_ROLE_USER = `user`
export const AI_MSG_ROLE_BOT = `assistant`
